import React, { useEffect, useState, useRef } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWS } from "@/contexts/WebSocketContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Plus, Move, Trash2, QrCode, Download, Copy, RefreshCw, X, Pencil, LayoutGrid, FileDown } from "lucide-react";

const STATUS_LABELS = {
    libero: "Libero",
    prenotato: "Prenotato",
    occupato: "Occupato",
    in_attesa: "In Cucina",
    pronti: "Pronti",
    conto: "Conto",
    pagato: "Pagato",
};

const STATUS_LEGEND = ["libero", "prenotato", "occupato", "in_attesa", "pronti", "conto"];

const TableShape = ({ table, onClick, selected, dragging, onMouseDown }) => {
    const shapeClass =
        table.shape === "round"
            ? "table-round"
            : table.shape === "rectangle"
            ? "table-rectangle"
            : "table-square";
    const statusClass = `status-${table.status}`;
    return (
        <div
            data-testid={`table-${table.number}`}
            onClick={onClick}
            onMouseDown={onMouseDown}
            className={`absolute border-2 ${shapeClass} ${statusClass} flex flex-col items-center justify-center cursor-pointer select-none transition-all hover:scale-[1.03] ${
                selected ? "ring-2 ring-brand" : ""
            } ${dragging ? "opacity-70" : ""}`}
            style={{
                left: table.x,
                top: table.y,
                width: table.width,
                height: table.height,
            }}
        >
            <div className="font-display font-bold text-2xl">{table.number}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">
                {table.seats}p
            </div>
        </div>
    );
};

const MappaSala = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { lastEvent } = useWS();
    const [rooms, setRooms] = useState([]);
    const [activeRoom, setActiveRoom] = useState(null);
    const [tables, setTables] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [selected, setSelected] = useState(null);
    const dragRef = useRef(null);
    const canvasRef = useRef(null);
    const [qrTable, setQrTable] = useState(null);
    const isAdmin = user.role === "admin";

    const tableUrl = (code) => `${window.location.origin}/c?t=${encodeURIComponent(code || "")}`;

    const downloadQR = (table) => {
        // Costruiamo un SVG personalizzato che include nome sala + numero tavolo + QR
        // così quando vengono stampati molteplici QR non sembrano duplicati.
        const qrSvgEl = document.querySelector(`[data-testid="mappa-qr-modal"] svg`);
        if (!qrSvgEl) return;
        const qrInner = new XMLSerializer().serializeToString(qrSvgEl);
        const roomName = activeRoom?.name || "";
        const W = 400;
        const H = 520;
        const wrapped = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  <rect width="${W}" height="${H}" fill="#ffffff"/>
  <text x="${W / 2}" y="55" font-family="Georgia, serif" font-size="20" fill="#0A1220" text-anchor="middle" font-weight="bold">SCANSIONA PER ORDINARE</text>
  <text x="${W / 2}" y="95" font-family="Arial, sans-serif" font-size="14" fill="#5C6675" text-anchor="middle" letter-spacing="2">${roomName.toUpperCase()}</text>
  <text x="${W / 2}" y="135" font-family="Georgia, serif" font-size="36" fill="#0A1220" text-anchor="middle" font-weight="bold">Tavolo ${table.number}</text>
  <g transform="translate(${(W - 240) / 2}, 165)">
    <rect width="240" height="240" fill="#ffffff" stroke="#E5E7EB"/>
    <g transform="translate(10, 10)">${qrInner.replace(/<\?xml[^>]*\?>/, "").replace(/^<svg[^>]*>/, "").replace(/<\/svg>\s*$/, "")}</g>
  </g>
  <text x="${W / 2}" y="445" font-family="monospace" font-size="14" fill="#0A1220" text-anchor="middle" letter-spacing="3">${table.short_code}</text>
  <text x="${W / 2}" y="475" font-family="Arial, sans-serif" font-size="11" fill="#5C6675" text-anchor="middle">Inquadra il QR oppure digita il codice nel portale clienti</text>
  <text x="${W / 2}" y="500" font-family="Arial, sans-serif" font-size="9" fill="#9CA3AF" text-anchor="middle">Smart Table by Renova360</text>
</svg>`;
        const blob = new Blob([wrapped], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const slug = roomName.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || "sala";
        a.download = `qr-${slug}-tavolo-${table.number}.svg`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const regenCode = async (table) => {
        if (!window.confirm("Rigenerare il codice annulla i QR già stampati. Procedere?")) return;
        try {
            const { data } = await api.post(`/tables/${table.id}/regenerate-code`);
            const updated = { ...table, short_code: data.short_code };
            setTables((prev) => prev.map((x) => (x.id === table.id ? updated : x)));
            setSelected((s) => (s && s.id === table.id ? updated : s));
            setQrTable(updated);
            toast.success("Codice rigenerato");
        } catch (e) { toast.error(formatError(e)); }
    };

    const loadRooms = async () => {
        const { data } = await api.get("/rooms");
        setRooms(data);
        if (data.length && !activeRoom) setActiveRoom(data[0]);
    };

    const loadTables = async (roomId) => {
        if (!roomId) return;
        const { data } = await api.get(`/tables?room_id=${roomId}`);
        setTables(data);
    };

    useEffect(() => { loadRooms(); }, []);
    useEffect(() => { if (activeRoom) loadTables(activeRoom.id); }, [activeRoom]);

    useEffect(() => {
        if (!lastEvent) return;
        if (lastEvent.type === "table_updated" && lastEvent.table) {
            setTables((prev) =>
                prev.map((t) => (t.id === lastEvent.table.id ? lastEvent.table : t))
            );
        }
    }, [lastEvent]);

    const [bisPrompt, setBisPrompt] = useState(null); // {table} se ordine attivo
    const [bisBusy, setBisBusy] = useState(false);

    const onTableClick = (t) => {
        if (editMode) {
            setSelected(t);
            return;
        }
        if (user.role === "kitchen") return;
        // Se il tavolo è OCCUPATO o ha un conto in corso, chiedi al cameriere se procedere col tavolo attuale
        // oppure liberare e creare un BIS per nuovi clienti
        const isWaiterOrCashier = user.role === "waiter" || user.role === "admin" || user.role === "cashier";
        if (isWaiterOrCashier && (t.status === "occupato" || t.status === "in_attesa" || t.status === "pronti" || t.status === "conto")) {
            setBisPrompt(t);
            return;
        }
        if (t.status === "libero" || t.status === "prenotato") {
            navigate(`/ordine/${t.id}`);
        } else if (t.status === "conto" && (user.role === "admin" || user.role === "cashier")) {
            navigate(`/cassa/${t.id}`);
        } else {
            navigate(`/ordine/${t.id}`);
        }
    };

    const proceedCurrentTable = () => {
        if (!bisPrompt) return;
        const t = bisPrompt;
        setBisPrompt(null);
        if (t.status === "conto" && (user.role === "admin" || user.role === "cashier")) {
            navigate(`/cassa/${t.id}`);
        } else {
            navigate(`/ordine/${t.id}`);
        }
    };

    const startBisOnTable = async () => {
        if (!bisPrompt) return;
        const t = bisPrompt;
        setBisBusy(true);
        try {
            const { data } = await api.post(`/tables/${t.id}/start-bis`, {});
            toast.success(data.message || `Tavolo ${data.bis_label} creato`);
            setBisPrompt(null);
            // Naviga al nuovo "ordine" che sarà aperto dal cameriere
            navigate(`/ordine/${t.id}`);
        } catch (e) { toast.error(formatError(e)); }
        setBisBusy(false);
    };

    const onMouseDown = (e, t) => {
        if (!editMode) return;
        e.preventDefault();
        const rect = canvasRef.current.getBoundingClientRect();
        dragRef.current = {
            id: t.id,
            offsetX: e.clientX - rect.left - t.x,
            offsetY: e.clientY - rect.top - t.y,
        };
    };

    const onMouseMove = (e) => {
        if (!dragRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = Math.max(0, e.clientX - rect.left - dragRef.current.offsetX);
        const y = Math.max(0, e.clientY - rect.top - dragRef.current.offsetY);
        setTables((prev) =>
            prev.map((t) => (t.id === dragRef.current.id ? { ...t, x, y } : t))
        );
    };

    const onMouseUp = async () => {
        if (!dragRef.current) return;
        const t = tables.find((x) => x.id === dragRef.current.id);
        dragRef.current = null;
        if (t) {
            try {
                await api.patch(`/tables/${t.id}`, { x: t.x, y: t.y });
            } catch (e) { toast.error(formatError(e)); }
        }
    };

    const addTable = async () => {
        if (!activeRoom) return;
        const num = String(tables.length + 1);
        try {
            // Non passare x/y → il backend calcola la posizione auto-layout (default 4 posti, quadrato 80x80)
            const { data } = await api.post("/tables", {
                room_id: activeRoom.id,
                number: num,
                seats: 4,
                shape: "square",
            });
            setTables((prev) => [...prev, data]);
        } catch (e) { toast.error(formatError(e)); }
    };

    const reorganize = async () => {
        if (!activeRoom) return;
        if (!window.confirm("Riorganizzare tutti i tavoli a griglia? Le posizioni attuali verranno ricalcolate.")) return;
        try {
            const { data } = await api.post(`/rooms/${activeRoom.id}/reorganize`);
            toast.success(`Riorganizzati ${data.moved} di ${data.total} tavoli`);
            // ricarica la sala
            const { data: ts } = await api.get(`/tables?room_id=${activeRoom.id}`);
            setTables(ts);
        } catch (e) { toast.error(formatError(e)); }
    };

    const downloadBlobPdf = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 200);
    };

    const downloadRoomQrPdf = async () => {
        if (!activeRoom) { toast.error("Nessuna sala selezionata"); return; }
        try {
            const resp = await api.get(`/rooms/${activeRoom.id}/qr-pdf`, { responseType: "blob" });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const slug = (activeRoom.name || "sala").toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
            downloadBlobPdf(blob, `qr-${slug}.pdf`);
            toast.success(`PDF QR di "${activeRoom.name}" scaricato`);
        } catch (e) { toast.error(formatError(e)); }
    };

    const downloadMenuQrPdf = async () => {
        if (!activeRoom) { toast.error("Nessuna sala selezionata"); return; }
        try {
            toast.info(`Generazione PDF QR Menu di "${activeRoom.name}"...`);
            const resp = await api.get(`/rooms/${activeRoom.id}/menu-qr-pdf`, { responseType: "blob" });
            if (!resp.data || resp.data.size === 0) { toast.error("PDF vuoto"); return; }
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const slug = (activeRoom.name || "sala").toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
            downloadBlobPdf(blob, `menu-qr-${slug}.pdf`);
            toast.success(`PDF QR Menu di "${activeRoom.name}" scaricato (${(blob.size/1024).toFixed(0)} KB)`);
        } catch (e) {
            console.error("downloadMenuQrPdf error:", e);
            toast.error(formatError(e));
        }
    };

    const downloadMenuQrPdfAll = async () => {
        try {
            toast.info("Generazione PDF di tutti i tavoli...");
            const resp = await api.get(`/tables/menu-qr-pdf-all`, { responseType: "blob" });
            if (!resp.data || resp.data.size === 0) { toast.error("PDF vuoto"); return; }
            const blob = new Blob([resp.data], { type: "application/pdf" });
            downloadBlobPdf(blob, `menu-qr-tutti-tavoli.pdf`);
            toast.success(`PDF QR Menu (TUTTE le sale) scaricato (${(blob.size/1024).toFixed(0)} KB)`);
        } catch (e) {
            console.error("downloadMenuQrPdfAll error:", e);
            toast.error(formatError(e));
        }
    };

    const updateSelected = async (patch) => {
        if (!selected) return;
        try {
            const { data } = await api.patch(`/tables/${selected.id}`, patch);
            setTables((prev) => prev.map((t) => (t.id === selected.id ? data : t)));
            setSelected(data);
        } catch (e) { toast.error(formatError(e)); }
    };

    const deleteSelected = async () => {
        if (!selected) return;
        if (!window.confirm("Eliminare il tavolo?")) return;
        try {
            await api.delete(`/tables/${selected.id}`);
            setTables((prev) => prev.filter((t) => t.id !== selected.id));
            setSelected(null);
        } catch (e) { toast.error(formatError(e)); }
    };

    const addRoom = async () => {
        const name = window.prompt("Nome sala");
        if (!name) return;
        try {
            const { data } = await api.post("/rooms", { name });
            setRooms((prev) => [...prev, data]);
            setActiveRoom(data);
        } catch (e) { toast.error(formatError(e)); }
    };

    const renameRoom = async (room) => {
        const name = window.prompt("Nuovo nome sala", room.name);
        if (!name || name.trim() === room.name) return;
        try {
            await api.patch(`/rooms/${room.id}`, { name: name.trim() });
            setRooms((prev) => prev.map((r) => r.id === room.id ? { ...r, name: name.trim() } : r));
            if (activeRoom?.id === room.id) setActiveRoom({ ...room, name: name.trim() });
            toast.success("Sala rinominata");
        } catch (e) { toast.error(formatError(e)); }
    };

    const deleteRoom = async (room) => {
        const tablesInRoom = tables.filter((t) => t.room_id === room.id);
        const occupied = tablesInRoom.filter((t) => t.status !== "libero");
        if (occupied.length > 0) {
            toast.error(`Impossibile eliminare: ${occupied.length} tavoli sono ancora occupati o prenotati`);
            return;
        }
        const msg = tablesInRoom.length > 0
            ? `Eliminare la sala "${room.name}" e i suoi ${tablesInRoom.length} tavoli (tutti liberi)?`
            : `Eliminare la sala "${room.name}"?`;
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/rooms/${room.id}`);
            const remaining = rooms.filter((r) => r.id !== room.id);
            setRooms(remaining);
            setTables((prev) => prev.filter((t) => t.room_id !== room.id));
            if (activeRoom?.id === room.id) setActiveRoom(remaining[0] || null);
            toast.success("Sala eliminata");
        } catch (e) { toast.error(formatError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="mappa-sala-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Sala</div>
                    <h1 className="font-display text-4xl font-bold text-warm">Mappa Tavoli</h1>
                </div>
                <div className="flex flex-wrap gap-2">
                    {rooms.map((r) => {
                        const isActive = activeRoom?.id === r.id;
                        return (
                            <div key={r.id} className={`flex items-stretch rounded-md border overflow-hidden ${isActive ? "border-brand" : "border-[#1f2c44] hover:border-brand"}`}>
                                <button
                                    onClick={() => setActiveRoom(r)}
                                    data-testid={`room-${r.id}`}
                                    className={`px-4 h-12 text-sm font-medium ${isActive ? "bg-brand/10 text-brand" : "text-elegant"}`}
                                >
                                    {r.name}
                                </button>
                                {isAdmin && isActive && (
                                    <>
                                        <button
                                            onClick={() => renameRoom(r)}
                                            data-testid={`rename-room-${r.id}`}
                                            title="Rinomina sala"
                                            className="px-2 border-l border-[#1f2c44] text-elegant hover:text-brand hover:bg-brand/5 flex items-center"
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => deleteRoom(r)}
                                            data-testid={`delete-room-${r.id}`}
                                            title="Elimina sala"
                                            className="px-2 border-l border-[#1f2c44] text-elegant hover:text-red-400 hover:bg-red-500/10 flex items-center"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    {isAdmin && (
                        <>
                            <button onClick={addRoom} data-testid="add-room-button" className="px-4 h-12 rounded-md border border-[#1f2c44] text-elegant hover:border-brand flex items-center gap-2">
                                <Plus className="w-4 h-4" /> Sala
                            </button>
                            <button
                                onClick={() => { setEditMode(!editMode); setSelected(null); }}
                                data-testid="edit-mode-toggle"
                                className={`px-4 h-12 rounded-md border text-sm font-medium flex items-center gap-2 ${
                                    editMode ? "bg-brand text-deep border-brand" : "border-[#1f2c44] text-elegant hover:border-brand"
                                }`}
                            >
                                <Move className="w-4 h-4" />
                                {editMode ? "Salva Layout" : "Modifica Layout"}
                            </button>
                            {activeRoom && tables.length > 0 && !editMode && (
                                <>
                                    <button
                                        onClick={downloadRoomQrPdf}
                                        data-testid="download-room-qr-pdf"
                                        title={`Stampa un PDF con tutti i QR dei tavoli di "${activeRoom.name}" (12 per pagina)`}
                                        className="px-4 h-12 rounded-md border border-purple-500/50 text-purple-300 hover:bg-purple-900/20 flex items-center gap-2"
                                    >
                                        <FileDown className="w-4 h-4" /> PDF QR Sala
                                    </button>
                                    <button
                                        onClick={downloadMenuQrPdf}
                                        data-testid="download-menu-qr-pdf"
                                        title={`Stampa un PDF con i QR del menu pubblico per ogni tavolo di "${activeRoom.name}" (12 per pagina, con logo)`}
                                        className="px-4 h-12 rounded-md border border-amber-500/50 text-amber-300 hover:bg-amber-900/20 flex items-center gap-2"
                                    >
                                        <FileDown className="w-4 h-4" /> PDF QR Menu
                                    </button>
                                    <button
                                        onClick={downloadMenuQrPdfAll}
                                        data-testid="download-menu-qr-pdf-all"
                                        title="Stampa un PDF con i QR del menu di TUTTI i tavoli di TUTTE le sale (12 per pagina A4, con logo)"
                                        className="px-4 h-12 rounded-md bg-amber-500 hover:bg-amber-400 text-deep font-bold flex items-center gap-2 transition-colors"
                                    >
                                        <FileDown className="w-4 h-4" /> PDF QR Menu (TUTTI)
                                    </button>
                                </>
                            )}
                            {editMode && (
                                <>
                                    <button onClick={addTable} data-testid="add-table-button" className="px-4 h-12 rounded-md bg-brand text-deep font-bold flex items-center gap-2">
                                        <Plus className="w-4 h-4" /> Tavolo
                                    </button>
                                    <button onClick={reorganize} data-testid="reorganize-button" className="px-4 h-12 rounded-md border border-brand/50 text-brand hover:bg-brand/10 flex items-center gap-2" title="Risistema tutti i tavoli a griglia">
                                        <LayoutGrid className="w-4 h-4" /> Riorganizza
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3">
                {STATUS_LEGEND.map((s) => (
                    <div key={s} className={`status-${s} px-3 py-1 rounded text-xs uppercase tracking-wider border-2`}>
                        {STATUS_LABELS[s]}
                    </div>
                ))}
            </div>

            <div className="flex flex-col lg:flex-row gap-4">
                <div
                    ref={canvasRef}
                    className="flex-1 relative bg-[#0e0e0f] border border-[#1f2c44] rounded-lg overflow-auto"
                    style={{ minHeight: 700 }}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    data-testid="room-canvas"
                >
                    <div className="absolute inset-0" style={{
                        backgroundImage: "linear-gradient(rgba(197,160,89,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(197,160,89,0.04) 1px, transparent 1px)",
                        backgroundSize: "40px 40px",
                    }} />
                    <div className="relative" style={{ width: activeRoom?.width || 1000, height: activeRoom?.height || 700 }}>
                        {tables.map((t) => (
                            <TableShape
                                key={t.id}
                                table={t}
                                selected={selected?.id === t.id}
                                dragging={dragRef.current?.id === t.id}
                                onClick={() => onTableClick(t)}
                                onMouseDown={(e) => onMouseDown(e, t)}
                            />
                        ))}
                    </div>
                </div>

                {editMode && selected && (
                    <div className="lg:w-80 bg-surface border border-[#1f2c44] rounded-lg p-5 h-fit">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-brand mb-2">Tavolo Selezionato</div>
                        <div className="font-display text-2xl font-bold mb-4">N° {selected.number}</div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-elegant uppercase tracking-wider">Numero</label>
                                <input
                                    type="text"
                                    value={selected.number}
                                    onChange={(e) => setSelected({ ...selected, number: e.target.value })}
                                    onBlur={(e) => updateSelected({ number: e.target.value })}
                                    data-testid="table-number-input"
                                    className="w-full h-12 mt-1 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-elegant uppercase tracking-wider">Posti</label>
                                <input
                                    type="number"
                                    value={selected.seats}
                                    onChange={(e) => setSelected({ ...selected, seats: parseInt(e.target.value) || 1 })}
                                    onBlur={(e) => updateSelected({ seats: parseInt(e.target.value) || 1 })}
                                    data-testid="table-seats-input"
                                    className="w-full h-12 mt-1 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-elegant uppercase tracking-wider">Forma</label>
                                <div className="grid grid-cols-3 gap-2 mt-1">
                                    {["round", "square", "rectangle"].map((sh) => (
                                        <button
                                            key={sh}
                                            data-testid={`shape-${sh}`}
                                            onClick={() => updateSelected({ shape: sh, width: sh === "rectangle" ? 140 : 90, height: sh === "rectangle" ? 80 : 90 })}
                                            className={`h-12 rounded-md border text-xs uppercase tracking-wider ${
                                                selected.shape === sh ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44]"
                                            }`}
                                        >
                                            {sh === "round" ? "Tondo" : sh === "square" ? "Quadrato" : "Rett."}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <button
                                onClick={() => setQrTable(selected)}
                                data-testid="show-qr-button"
                                className="w-full h-12 mt-3 rounded-md border border-brand text-brand hover:bg-brand-soft flex items-center justify-center gap-2"
                            >
                                <QrCode className="w-4 h-4" /> QR per il cliente
                            </button>
                            <button
                                onClick={deleteSelected}
                                data-testid="delete-table-button"
                                className="w-full h-12 mt-3 rounded-md border border-red-700/50 text-red-400 hover:bg-red-900/20 flex items-center justify-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" /> Elimina Tavolo
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* QR modal */}
            {qrTable && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setQrTable(null)} data-testid="mappa-qr-modal">
                    <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-[#1f2c44] rounded-2xl p-8 max-w-sm w-full text-center">
                        <button onClick={() => setQrTable(null)} className="absolute right-6 top-6 text-elegant hover:text-warm" data-testid="close-qr-modal">
                            <X className="w-5 h-5" />
                        </button>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Tavolo</div>
                        <div className="font-display text-4xl font-bold mb-1">N° {qrTable.number}</div>
                        {activeRoom?.name && (
                            <div className="text-xs uppercase tracking-[0.25em] text-elegant mb-4" data-testid="qr-modal-room">
                                {activeRoom.name}
                            </div>
                        )}
                        <div className="bg-white p-4 rounded-md inline-flex">
                            <QRCodeSVG value={tableUrl(qrTable.short_code)} size={220} bgColor="#ffffff" fgColor="#0A1220" level="H" />
                        </div>
                        <div className="font-mono text-sm tracking-wider text-warm mt-4 break-all" data-testid="qr-modal-code">{qrTable.short_code}</div>
                        <div className="text-xs text-elegant mt-2">Inquadra il QR oppure digita il codice nel portale clienti.</div>
                        <div className="grid grid-cols-2 gap-2 mt-5">
                            <button onClick={() => { navigator.clipboard.writeText(tableUrl(qrTable.short_code)); toast.success("Link copiato"); }} data-testid="copy-qr-modal"
                                className="h-11 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md flex items-center justify-center gap-2 text-sm">
                                <Copy className="w-4 h-4" /> Copia link
                            </button>
                            <button onClick={() => downloadQR(qrTable)} data-testid="download-qr-modal"
                                className="h-11 bg-brand hover:bg-brand-light text-deep font-bold rounded-md flex items-center justify-center gap-2 text-sm">
                                <Download className="w-4 h-4" /> Scarica QR
                            </button>
                        </div>
                        <button onClick={() => regenCode(qrTable)} data-testid="regen-qr-modal" className="mt-3 text-[11px] uppercase tracking-wider text-elegant hover:text-red-400 flex items-center justify-center gap-1 w-full">
                            <RefreshCw className="w-3 h-3" /> Rigenera codice
                        </button>
                    </div>
                </div>
            )}

            {/* Modal scelta: procedi col tavolo attuale o crea BIS */}
            {bisPrompt && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !bisBusy && setBisPrompt(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-surface border-2 border-amber-500/60 rounded-2xl p-6 max-w-md w-full" data-testid="bis-modal">
                        <div className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-1">Tavolo già occupato</div>
                        <div className="font-display text-2xl font-bold mb-1">Tavolo {bisPrompt.number}</div>
                        <div className="text-xs text-elegant mb-5">
                            Questo tavolo ha già un ordine attivo (stato: <strong className="text-warm">{bisPrompt.status}</strong>).
                            Vuoi continuare a lavorare sull&apos;ordine in corso, oppure liberare il tavolo (lasciando il conto in attesa di pagamento)
                            e cominciare un nuovo turno per nuovi clienti?
                        </div>

                        <div className="space-y-3">
                            <button onClick={proceedCurrentTable} disabled={bisBusy}
                                data-testid="bis-proceed-current"
                                className="w-full h-14 bg-brand text-deep font-bold rounded-md uppercase tracking-wider text-sm flex flex-col items-center justify-center transition-colors">
                                <span>Procedi con tavolo attuale</span>
                                <span className="text-[10px] font-normal opacity-70">Aggiungi piatti / modifica conto esistente</span>
                            </button>
                            <button onClick={startBisOnTable} disabled={bisBusy}
                                data-testid="bis-create-new"
                                className="w-full h-14 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-md uppercase tracking-wider text-sm flex flex-col items-center justify-center transition-colors disabled:opacity-60">
                                <span>{bisBusy ? "Creo..." : `Libera e crea Tavolo ${bisPrompt.number}-BIS`}</span>
                                <span className="text-[10px] font-normal opacity-80">Il conto precedente resta in attesa in cassa</span>
                            </button>
                            <button onClick={() => setBisPrompt(null)} disabled={bisBusy}
                                data-testid="bis-cancel"
                                className="w-full h-10 text-elegant text-xs uppercase tracking-wider hover:text-warm">
                                Annulla
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MappaSala;
